import {
  Button,
  DiamondField,
  FlowerIcon,
  Icon,
  InputVariant,
  Link,
  Modal,
  SendIcon,
  TextField,
  XIcon,
} from '@/components';
import { yupResolver } from '@hookform/resolvers/yup';
import { TFunction } from 'next-i18next';
import React, { forwardRef, useCallback, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { getSession, useAddCommentQuery, useGiftAtd, useSendSticker } from '../../../api';
import { CollectionStickerType, FormChatDefault, LiveStreamStatus, StickerType } from '../../../const';
import * as yup from 'yup';
import { AnimatePresence, motion } from 'framer-motion';
import SendSticker from '../SendSticker';
import { PagePath } from '@/layouts/shared';

type SendCommentProps = {
  userId: number;
  liveStreamId: number;
  t: TFunction;
  status: string;
  collectionStickers: CollectionStickerType[];
  setDisabled: () => void;
  isAuth: boolean;
  onShowProducts: () => void;
};
// eslint-disable-next-line react/display-name
export const SendCommentBlock = forwardRef((props: SendCommentProps, ref) => {
  const { userId, liveStreamId, t, status, collectionStickers, setDisabled, isAuth, onShowProducts } = props;
  const [showSticker, setShowSticker] = useState<boolean>(false);
  const [show, setShow] = useState<boolean>(false);
  const [isLoadingGift, setLoadingGift] = useState<boolean>(false);

  const commentRef = useRef<any>(null);
  const { mutate, isLoading } = useAddCommentQuery({
    onSuccess: () => {
      reset();
      toast.success(t('detail.message.add-comment-success'));
      setTimeout(() => {
        setDisabled();
      }, 5000);
    },
  });
  const { mutate: mutateGiftAtd } = useGiftAtd({
    onSuccess: () => {
      toast.success('Cảm ơn bạn đã tặng atd cho An Thư. Chúc bạn một ngày tốt lành');
    },
    onError: ({ message }) => {
      toast.error(message);
    },
  });
  const {
    reset,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<any>({
    defaultValues: FormChatDefault,
    resolver: yupResolver(
      yup.object({
        comment: yup.string().trim().required(t('detail.message.comment-required')),
      })
    ),
  });
  const onCommentClick = handleSubmit((data) => {
    if (isLoading || !userId) return;
    mutate({
      ...data,
      comment: data.comment,
      user_id: userId || 0,
      live_stream_id: +liveStreamId,
      images: null,
    });
    commentRef.current && commentRef.current.scrollToBottom();
  });
  const keyPress = useCallback(
    (e: any) => {
      if (e.keyCode === 13) {
        if (!isAuth) return setShow(true);
        return onCommentClick();
      }
    },
    [isAuth, onCommentClick]
  );
  // const keyPress = (e: any) => {
  //   if (e.keyCode === 13) {
  //     onCommentClick();
  //   }
  // };
  const onStickerClick = () => {
    setShowSticker(!showSticker);
  };
  const { mutate: SendStickerMutate, isLoading: isLoadingSendSticker } = useSendSticker({
    onSuccess: () => {
      setShowSticker(false);
    },
  });
  useImperativeHandle(ref, () => ({
    sendComment: (data: any) => {
      mutate(data);
    },
  }));
  const sendSticker = (s: StickerType) => {
    const payload = {
      live_stream_id: liveStreamId,
      sticker_id: s.id,
      sticker_collection_id: s.collection_id,
    };
    setShowSticker(false);
    SendStickerMutate(payload);
  };
  const giftAtd = async (amount: number) => {
    setLoadingGift(true);

    try {
      const { data } = await getSession();
      await mutateGiftAtd({ amount, liveId: liveStreamId, sessionId: data.session_id });
    } catch (_) {
    } finally {
      setLoadingGift(false);
      setShowSticker(false);
    }
  };
  return (
    <div className="tw-relative">
      <AnimatePresence>
        {showSticker && (
          <motion.div
            initial="stickerWrapperInitial"
            exit="stickerWrapperExit"
            animate="stickerWrapperAnimate"
            transition={{ duration: 0.3 }}
            variants={{
              stickerWrapperInitial: {
                opacity: 0,
              },
              stickerWrapperExit: {
                opacity: 0,
              },
              stickerWrapperAnimate: {
                opacity: 1,
              },
            }}
          >
            <div className="tw-absolute bottom-100-12 tw-left-0 tw-w-full tw-bg-white tw-rounded-5 tw-drop-shadow-lg custom-polygon-sticker">
              <div className="tw-relative tw-w-full">
                <div
                  className="tw-absolute tw-text-secondary-70 tw-top-3 tw-right-3 tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-cursor-pointer"
                  onClick={() => setShowSticker(false)}
                >
                  <Icon className="tw-w-3 tw-h-3" icon={<XIcon />} />
                </div>

                <div className="tw-w-full tw-py-2">
                  <SendSticker
                    giftAtd={giftAtd}
                    t={t}
                    isLoading={isLoadingSendSticker || isLoadingGift}
                    collectionStickers={collectionStickers}
                    sendSticker={sendSticker}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="tw-flex tw-gap-x-2 tw-items-center ">
        <Button
          loading={isLoading}
          onClick={onShowProducts}
          className="tw-flex tw-justify-center tw-p-2 !tw-rounded-full !tw-w-fit tw-bg-primary-main"
        >
          <Icon className="tw-w-4 tw-h-4" icon={<DiamondField />} />
        </Button>
        <div className="tw-flex-1">
          <TextField
            name="comment"
            control={control}
            showErrorMessage={false}
            variant={InputVariant.Outlined}
            error={errors.comment?.message}
            onKeyDown={keyPress}
            placeholder={t('detail.form.label.comment') + '...'}
            containerClassName="tw-bg-white tw-rounded-full !tw-border-0 tw-border-white  tw-flex-1"
            className="tw-px-2 custom-placeholder  "
          />
        </div>

        <Button
          disabled={!userId}
          loading={isLoading}
          onClick={onCommentClick}
          className="tw-flex tw-justify-center tw-p-2 !tw-rounded-full !tw-w-fit tw-bg-primary-main"
        >
          <Icon className="tw-w-4 tw-h-4" icon={<SendIcon />} />
        </Button>
        {status === LiveStreamStatus.LIVE_NOW && !!collectionStickers.length && (
          <Button
            disabled={!userId}
            loading={isLoading}
            onClick={onStickerClick}
            className="tw-flex tw-justify-center tw-p-2 !tw-rounded-full !tw-w-fit tw-bg-primary-main"
          >
            <Icon className="tw-w-4 tw-h-4" icon={<FlowerIcon />} />
          </Button>
        )}
      </div>
      {show && (
        <Modal onClose={() => setShow(false)}>
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
            <div className="tw-text-center">Vui lòng đăng nhập để sử dụng tính năng này.</div>
            <Link className="tw-mt-4 !tw-underline tw-text-lg" url={{ pathname: PagePath.LoginPage }}>
              Đăng nhập
            </Link>
          </div>
        </Modal>
      )}
    </div>
  );
});
